import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

export const HOW_HEARD_OPTIONS = [
  'Instagram',
  'Facebook',
  'YouTube',
  'Word of mouth',
  'Previous visit',
  'Google',
  'Sky TV',
  'Radio',
  'Dukie Tours',
  'Mobile apps',
  'Newsletter',
  'Flyer',
  'Digital advertising screen',
  'Other',
];

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrap = styled.div`
  width: min(520px, 100%);
  padding: 0 1.15rem 0.5rem;
  animation: ${fadeIn} 0.4s cubic-bezier(0.22, 1, 0.36, 1);
  text-align: center;
`;

const Status = styled.p`
  margin: 0 0 0.85rem;
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.42);
  font-weight: 600;
`;

const Title = styled.h2`
  margin: 0 0 0.35rem;
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.15rem, 4.2vw, 1.45rem);
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  margin: 0 0 1.15rem;
  font-size: 0.86rem;
  color: rgba(16, 29, 64, 0.5);
  line-height: 1.45;
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.45rem;
  margin-bottom: 0.85rem;
`;

const Option = styled.button`
  border: 1px solid ${props => (props.$active ? 'rgba(16, 29, 64, 0.88)' : 'rgba(16, 29, 64, 0.12)')};
  background: ${props => (props.$active ? props.theme.colors.primary : 'rgba(255, 255, 255, 0.86)')};
  color: ${props => (props.$active ? 'white' : 'rgba(16, 29, 64, 0.82)')};
  border-radius: 999px;
  padding: 0.55rem 0.9rem;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
  min-height: 40px;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const OtherRow = styled.form`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  width: 100%;
  margin: 0.15rem 0 0.7rem;
  padding: 0.28rem 0.28rem 0.28rem 0.95rem;
  border-radius: 999px;
  background: white;
  border: 1px solid rgba(16, 29, 64, 0.14);
  box-shadow: 0 8px 24px rgba(16, 29, 64, 0.05);

  &:focus-within {
    border-color: rgba(16, 29, 64, 0.35);
  }
`;

const OtherInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 0.55rem 0;
  font-size: 16px;
  font-family: inherit;
  color: ${props => props.theme.colors.primary};

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: rgba(16, 29, 64, 0.38);
  }
`;

const SendBtn = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: ${props => (props.disabled ? 'rgba(16, 29, 64, 0.1)' : props.theme.colors.primary)};
  color: ${props => (props.disabled ? 'rgba(16, 29, 64, 0.28)' : 'white')};
  font-size: 1.05rem;
  line-height: 1;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
`;

const SkipBtn = styled.button`
  border: none;
  background: transparent;
  color: rgba(16, 29, 64, 0.42);
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0.4rem 0.6rem;
  letter-spacing: 0.01em;
`;

/**
 * Shown while a booking is being confirmed.
 * One tap submits any option except Other, which needs a short note first.
 */
const HowHeardSurvey = ({ confirming = true, onComplete }) => {
  const [source, setSource] = useState('');
  const [otherText, setOtherText] = useState('');

  const canSendOther = otherText.trim().length > 0;

  const choose = (opt) => {
    setSource(opt);
    if (opt !== 'Other') {
      onComplete({ source: opt, otherText: '' });
    }
  };

  const submitOther = (e) => {
    e?.preventDefault?.();
    const note = otherText.trim();
    if (!note) return;
    onComplete({ source: 'Other', otherText: note });
  };

  return (
    <Wrap>
      <Status>{confirming ? 'Confirming your booking' : 'One last thing'}</Status>
      <Title>How did you hear about us?</Title>
      <Subtitle>Tap one — then we’ll take you to your confirmation.</Subtitle>

      <Grid>
        {HOW_HEARD_OPTIONS.map((opt) => (
          <Option
            key={opt}
            type="button"
            $active={source === opt}
            onClick={() => choose(opt)}
          >
            {opt}
          </Option>
        ))}
      </Grid>

      {source === 'Other' && (
        <OtherRow onSubmit={submitOther}>
          <OtherInput
            type="text"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder="Tell us in a few words"
            autoFocus
            maxLength={200}
            aria-label="How you heard about us"
          />
          <SendBtn type="submit" disabled={!canSendOther} aria-label="Continue">
            →
          </SendBtn>
        </OtherRow>
      )}

      <SkipBtn type="button" onClick={() => onComplete(null)}>
        Skip
      </SkipBtn>
    </Wrap>
  );
};

export default HowHeardSurvey;
